/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';
import axios from '../../CustomAxios';
import Loading from '../components/Loading';
import * as _ from 'lodash';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import {
  Alert,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Grid,
  IconButton,
  Link,
  Paper as MuiPaper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  Divider as MuiDivider,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { spacing } from '@mui/system';
import { withStyles } from '@mui/styles';
import {
  Download,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import Filter from './Filter';

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Spacer = styled.div`flex: 1 1 100%;`;
const ToolbarTitle = styled.div`min-width: 150px;`;
const StyledTableContainer = withStyles(() => ({}))(TableContainer);

function createData(
  jobId,
  challanName,
  quantity,
  type,
  createdAt,
  plateBrand,
  plateTechnology,
  plateHeight,
  plateWidth,
  plateThickness,
  apCompany,
  customerCompany,
  actionBy,   // <-- new parameter
  rawData
) {
  return {
    jobId,
    challanName,
    quantity,
    type,
    createdAt,
    plateBrand,
    plateTechnology,
    plateHeight,
    plateWidth,
    plateThickness,
    apCompany,
    customerCompany,
    actionBy,   // <-- included in the returned object
    rawData,
  };
}

const headCells = [
  { id: 'jobId', label: 'Job ID', align: 'left' },
  { id: 'challanName', label: 'Challan Name', align: 'left' },
  { id: 'plate', label: 'Plate (Brand / Technology)', align: 'left' },
  { id: 'apCompany', label: 'AP Company', align: 'left' },
  { id: 'customerCompany', label: 'Customer Company', align: 'left' },
  { id: 'quantity', label: 'Quantity', align: 'left' },
  { id: 'createdAt', label: 'Created At', align: 'left' },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar({
  onSearch,
  search,
  setSearch,
  onFilterClick,
  filterCount,
  csvData,
  csvHeaders,
  csvFilename,
  apCompanies,
  selectedApCompany,
  setSelectedApCompany,
  onApCompanyChange,
}) {
  return (
    <Toolbar>
      <ToolbarTitle>
        <Typography variant="h6">Stock Auditing</Typography>
      </ToolbarTitle>
      <Spacer />
      {apCompanies && (
        <FormControl sx={{ minWidth: 200, mr: 2 }} size="small">
          <InputLabel id="ap-company-label">AP Company</InputLabel>
          <Select
            labelId="ap-company-label"
            id="ap-company-select"
            label="AP Company"
            value={selectedApCompany}
            onChange={(e) => {
              setSelectedApCompany(e.target.value);
              if (onApCompanyChange) onApCompanyChange(e.target.value);
            }}
          >
            <MenuItem disabled value="">
              Select AP Company
            </MenuItem>
            {apCompanies.map((company) => (
              <MenuItem key={company._id} value={company._id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {csvData && csvData.length > 0 && (
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename={csvFilename}
          style={{ textDecoration: 'none', marginRight: 16 }}
        >
          <Button variant="outlined" endIcon={<Download />}>
            CSV
          </Button>
        </CSVLink>
      )}
      <Tooltip title="Filters">
        <Badge badgeContent={filterCount} color="error">
          <IconButton onClick={onFilterClick} size="small">
            <FilterListIcon />
          </IconButton>
        </Badge>
      </Tooltip>
    </Toolbar>
  );
}

function StockAuditing({ auths }) {
  const location = useLocation();
  const navigate = useNavigate();
  const authData = JSON.parse(localStorage.getItem('AuthData'));
  const [loader, setLoader] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState('');
  const snackRef = useRef();

  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [jobId, setJobId] = useState(searchParams.get('job_id') || '');
  const [challanId, setChallanId] = useState(searchParams.get('challan_id') || '');
  const [type, setType] = useState(searchParams.get('type') || '');
  const [fromDate, setFromDate] = useState(searchParams.get('from_date') || '');
  const [toDate, setToDate] = useState(searchParams.get('to_date') || '');
  const [plateId, setPlateId] = useState(searchParams.get('plate_id') || '');
  const [selectedApCompany, setSelectedApCompany] = useState(
    searchParams.get('ap_company_id') || ''
  );
  const [customerCompanyId, setCustomerCompanyId] = useState(
    searchParams.get('customer_company_id') || ''
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);

  const [apCompanies, setApCompanies] = useState([]);

  const [page, setPage] = useState(
    searchParams.get('page') ? Number(searchParams.get('page')) : 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    searchParams.get('limit') ? Number(searchParams.get('limit')) : 25
  );
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('createdAt');

  const [csvData, setCsvData] = useState([]);
  const csvHeaders = [
    { label: 'Job ID', key: 'jobId' },
    { label: 'Challan Name', key: 'challanName' },
    { label: 'Plate (Brand / Technology)', key: 'plate' },
    { label: 'AP Company', key: 'apCompany' },
    { label: 'Customer Company', key: 'customerCompany' },
    { label: 'Quantity', key: 'quantity' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Action By', key: 'actionBy' },
  ];
  const csvFilename = `StockAuditing_${moment().format('DD-MM-YY_HH-mm')}.csv`;

  // ---- Helper: sync URL with current state ----
  const updateURL = () => {
    const params = new URLSearchParams();
    if (jobId) params.set('job_id', jobId);
    if (challanId) params.set('challan_id', challanId);
    if (type) params.set('type', type);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    if (plateId) params.set('plate_id', plateId);
    if (selectedApCompany) params.set('ap_company_id', selectedApCompany);
    if (customerCompanyId) params.set('customer_company_id', customerCompanyId);
    params.set('page', page);
    params.set('limit', rowsPerPage);
    navigate(`/dashboard/stock-auditing?${params.toString()}`, { replace: true });
  };

  // ---- Filter count ----
  useEffect(() => {
    let count = 0;
    if (jobId) count++;
    if (challanId) count++;
    if (type) count++;
    if (fromDate) count++;
    if (toDate) count++;
    if (plateId) count++;
    if (selectedApCompany) count++;
    if (customerCompanyId) count++;
    setFilterCount(count);
  }, [jobId, challanId, type, fromDate, toDate, plateId, selectedApCompany, customerCompanyId]);

  // ---- Fetch AP Companies ----
  const fetchApCompanies = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const url = `/api/v1/${authData.type}/ap_company?limit=1000&is_table=true`;
      const { data } = await axios.get(url, config);
      if (data.success) {
        setApCompanies(data.data.data || data.data);
      }
    } catch (e) {
      console.error('Error fetching AP companies', e);
    }
  };

  useEffect(() => {
    fetchApCompanies();
  }, []);

  // ---- Default AP Company ----
  useEffect(() => {
    if (apCompanies.length > 0 && !selectedApCompany) {
      setSelectedApCompany(apCompanies[0]._id);
    }
  }, [apCompanies, selectedApCompany]);

  // ---- Fetch stock auditing data ----
  const fetchStockAuditing = async () => {
    setLoader(true);
    try {
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      const params = new URLSearchParams();
      if (jobId) params.append('job_id', jobId);
      if (challanId) params.append('challan_id', challanId);
      if (type) params.append('type', type);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (plateId) params.append('plate_id', plateId);
      if (selectedApCompany) params.append('ap_company_id', selectedApCompany);
      if (customerCompanyId) params.append('customer_company_id', customerCompanyId);
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);
      if (orderBy) params.append('sort_by', orderBy);
      params.append('order', order);

      const url = `/api/v1/${authData.type}/stock_auditing?${params.toString()}`;
      const { data } = await axios.get(url, config);
      if (data.success) {
        const audits = data.data.data;
        setTotal(data.data.metadata.total);
        const formatted = audits.map((item) => {
          const plateInfo = item.plate_info || {};
          const brand = plateInfo.brand?.name || '';
          const tech = plateInfo.technology?.name || '';
          const height = plateInfo.height || '';
          const width = plateInfo.width || '';
          const thickness = plateInfo.thickness || '';
          const apCompanyName = item.ap_company_info?.name || 'N/A';
          const customerCompanyName = item.customer_company_info?.name || 'N/A';

          return createData(
            item.job_info?.job_id || item.job_id || 'N/A',
            item.challan_info?.name || 'N/A',
            item.quantity,
            item.type,
            moment(item.createdAt).format('DD-MM-YYYY HH:mm'),
            brand,
            tech,
            height,
            width,
            thickness,
            apCompanyName,
            customerCompanyName,
            item.action_by_user_name || '--',   // <-- pass the resolved user name
            item
          );
        });
        setRows(formatted);

        setCsvData(
          formatted.map((r) => ({
            jobId: r.jobId,
            challanName: r.challanName,
            plate: `${r.plateHeight}X${r.plateWidth}X${r.plateThickness} | ${r.plateBrand} | ${r.plateTechnology}`,
            apCompany: r.apCompany,
            customerCompany: r.customerCompany,
            quantity: r.type === 'debit' ? `-${r.quantity}` : `+${r.quantity}`,
            createdAt: r.createdAt,
            actionBy: r.actionBy || '--',   // <-- now reads the row's actionBy property
          }))
        );
      }
    } catch (error) {
      console.error(error);
      setErr(error?.response?.data?.error || 'Failed to fetch stock auditing records');
      snackRef.current?.click();
    } finally {
      setLoader(false);
    }
  };

  // ---- Effect that runs only to fetch data (not to update URL) ----
  useEffect(() => {
    if (selectedApCompany) {
      fetchStockAuditing();
    }
  }, [page, rowsPerPage, order, orderBy, selectedApCompany, jobId, challanId, type, fromDate, toDate, plateId, customerCompanyId]);

  // ---- Handlers that change filters/pagination AND update URL ----
  const handleFilterApply = (filters) => {
    setJobId(filters.jobId || '');
    setChallanId(filters.challanId || '');
    setType(filters.type || '');
    setFromDate(filters.fromDate || '');
    setToDate(filters.toDate || '');
    setPlateId(filters.plateId || '');
    setCustomerCompanyId(filters.customerCompanyId || '');
    setPage(0);
    setFilterOpen(false);
    updateURL(); // update URL after state changes (they will trigger effect)
  };

  const handleApCompanyChange = (newValue) => {
    setSelectedApCompany(newValue);
    setPage(0);
    updateURL();
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    updateURL();
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    updateURL();
  };

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    updateURL();
  };

  if (loader && rows.length === 0) return <Loading />;

  return (
    <React.Fragment>
      <Helmet title="Alpha Plus - Stock Auditing" />
      <Grid justifyContent="space-between" alignItems="center" container spacing={10} mb={4}>
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline" sx={{ fontWeight: 600 }} color="primary">
            STOCK AUDITING
          </Typography>
        </Grid>
        <Grid item>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/dashboard/tasks">Dashboard</Link>
            <Typography>Stock Auditing</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Snackbar
        ref={snackRef}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!err}
        autoHideDuration={6000}
        onClose={() => setErr('')}
      >
        <Alert severity="error">{err}</Alert>
      </Snackbar>

      <Paper>
        <EnhancedTableToolbar
          onSearch={null}  // search currently not wired, can be added later
          search={search}
          setSearch={setSearch}
          onFilterClick={() => setFilterOpen(true)}
          filterCount={filterCount}
          csvData={csvData}
          csvHeaders={csvHeaders}
          csvFilename={csvFilename}
          apCompanies={apCompanies}
          selectedApCompany={selectedApCompany}
          setSelectedApCompany={setSelectedApCompany}
          onApCompanyChange={handleApCompanyChange}
        />
        <StyledTableContainer>
          <Table size="small">
            <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
            <TableBody>
              {rows.map((row, idx) => {
                const isDebit = row.type === 'debit';
                const quantityDisplay = isDebit ? `-${row.quantity}` : `+${row.quantity}`;
                const quantityColor = isDebit ? '#f44336' : '#4caf50';
                return (
                  <TableRow key={idx} hover>
                    <TableCell align="left">{row.jobId}</TableCell>
                    <TableCell align="left">{row.challanName}</TableCell>
                    <TableCell align="left">
                      {row.plateHeight || row.plateWidth || row.plateThickness
                        ? `${row.plateHeight || '-'}X${row.plateWidth || '-'}X${row.plateThickness || '-'} | ${row.plateBrand || '-'} | ${row.plateTechnology || '-'}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="left">{row.apCompany}</TableCell>
                    <TableCell align="left">{row.customerCompany}</TableCell>
                    <TableCell align="left">
                      <span style={{ color: quantityColor, fontWeight: 'bold' }}>{quantityDisplay}</span>
                    </TableCell>
                    <TableCell align="left">{row.createdAt}</TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No records found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Filter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleFilterApply}
        initialValues={{
          jobId,
          challanId,
          type,
          fromDate,
          toDate,
          plateId,
          customerCompanyId,
        }}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({ auths: state.auths });
export default connect(mapStateToProps, null)(StockAuditing);